/**
 * Streams `POST /api/v1/chat/message` and dispatches typed callbacks for each
 * server-sent event.
 *
 * Uses `fetch` + `response.body.getReader()` because `EventSource` only supports
 * GET. Cookies are sent via `credentials: "include"` and the CSRF check is
 * satisfied by the `X-Requested-With` header.
 */

const CHAT_ENDPOINT = "/api/v1/chat/message"

/** A prior conversation turn replayed so the assistant keeps context. */
export interface ChatHistoryItem {
  role: "user" | "assistant"
  content: string
}

export interface ChatSseCallbacks {
  onMode?: (mode: string, toolsCount: number) => void
  onChunk?: (delta: string) => void
  onToolCallStart?: (name: string, iteration: number) => void
  onToolCallResult?: (name: string, ok: boolean, durationMs: number) => void
  onDone?: (iterations: number) => void
  onError?: (code: string, message: string) => void
}

export class ChatSseAbort extends Error {
  constructor() {
    super("chat stream aborted")
  }
}

export function streamChatMessage(
  message: string,
  callbacks: ChatSseCallbacks,
  signal?: AbortSignal,
  history: ChatHistoryItem[] = [],
): Promise<void> {
  return doStream(message, callbacks, signal, history)
}

function doStream(
  message: string,
  callbacks: ChatSseCallbacks,
  signal: AbortSignal | undefined,
  history: ChatHistoryItem[],
): Promise<void> {
  const url = CHAT_ENDPOINT

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      Accept: "text/event-stream",
    },
    credentials: "include",
    body: JSON.stringify({ message, history }),
    signal,
  })
    .then((res) => handleResponse(res, callbacks))
    .catch((err) => {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new ChatSseAbort()
      }
      const message =
        err instanceof Error ? err.message : "Erreur réseau inattendue"
      callbacks.onError?.("network_error", message)
    })
}

function handleResponse(
  res: Response,
  callbacks: ChatSseCallbacks,
): Promise<void> {
  if (!res.ok) {
    return res
      .text()
      .then((body) => {
        const code = res.status === 401 ? "unauthorized" : "bad_request"
        let detail = body
        const parsed = safeJson(body)
        if (parsed && typeof parsed === "object") {
          const obj = parsed as Record<string, unknown>
          if (typeof obj.detail === "string") detail = obj.detail
          else if (typeof obj.message === "string") detail = obj.message
        }
        callbacks.onError?.(code, detail || `HTTP ${res.status}`)
      })
      .then(() => undefined)
  }

  if (!res.body) {
    callbacks.onError?.("network_error", "Le navigateur ne supporte pas le streaming.")
    return Promise.resolve()
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder("utf-8")
  let buffer = ""
  let currentEvent = ""
  let currentData = ""

  const pump = (): Promise<void> => {
    return reader.read().then(({ value, done }) => {
      if (done) {
        flushFrame(currentEvent, currentData, callbacks)
        return
      }
      buffer += decoder.decode(value, { stream: true })
      let boundary = buffer.indexOf("\n\n")
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + 2)
        ;[currentEvent, currentData] = parseFrame(frame)
        flushFrame(currentEvent, currentData, callbacks)
        currentEvent = ""
        currentData = ""
        boundary = buffer.indexOf("\n\n")
      }
      return pump()
    })
  }

  return pump()
}

function parseFrame(frame: string): [string, string] {
  let event = ""
  let data = ""
  for (const rawLine of frame.split("\n")) {
    const line = rawLine.trimStart()
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim()
    } else if (line.startsWith("data:")) {
      data += line.slice("data:".length).trim()
    }
  }
  return [event, data]
}

function flushFrame(
  event: string,
  data: string,
  callbacks: ChatSseCallbacks,
): void {
  if (!event && !data) return
  const parsed = safeJson(data) as Record<string, unknown> | null
  switch (event) {
    case "mode":
      callbacks.onMode?.(
        (parsed?.mode as string) ?? "unknown",
        (parsed?.tools_count as number) ?? 0,
      )
      break
    case "chunk":
      callbacks.onChunk?.((parsed?.delta as string) ?? "")
      break
    case "tool_call_start":
      callbacks.onToolCallStart?.(
        (parsed?.name as string) ?? "tool",
        (parsed?.iteration as number) ?? 1,
      )
      break
    case "tool_call_result":
      callbacks.onToolCallResult?.(
        (parsed?.name as string) ?? "tool",
        Boolean(parsed?.ok),
        (parsed?.duration_ms as number) ?? 0,
      )
      break
    case "done":
      callbacks.onDone?.((parsed?.iterations as number) ?? 1)
      break
    case "error":
      callbacks.onError?.(
        (parsed?.code as string) ?? "unknown",
        (parsed?.message as string) ?? "Erreur inconnue",
      )
      break
  }
}

function safeJson(input: string): unknown {
  if (!input) return null
  try {
    return JSON.parse(input)
  } catch {
    return null
  }
}
